// Transporte al cliente (Logística 2): lo que se cobra en una frase ("No se
// cobra transporte (Madrid, zona gratuita)" / "Se cobran 20 € de transporte +
// 30 € Madrid Central + IVA") con "Cambiar". Debajo de la frase, a la vista y
// antes de Madrid Central, va una fila rápida: si no se cobra transporte,
// "Cobrar transporte:" con 20, 30, 40 € y otro importe; si ya se cobra,
// "Transporte:" con los mismos botones (el actual resaltado), otro importe y
// "Quitar". Lo de la zona (precio por zona, volver al automático) solo está en
// el editor.
// Al pulsar "Cambiar" se despliega lo mismo
// que en Logística: cobrar sí o no, precio de la zona, otro importe (20, 30,
// 40 € o libre), usar el precio de la zona y Madrid Central (con regalar).
// Madrid Central se detecta solo por las coordenadas del lugar: dentro, el
// suplemento se añade automáticamente y aquí se puede quitar o volver a añadir.
// Mismos manejadores y endpoints (vienen de la ficha); nada de costes internos.
import React, { useState } from 'react';
import { eurosCorto, pistaMadridCentral, textoTransporteCliente } from '../../../utils/logistica2';
import { botonPrincipal, botonSecundario, enlace, enlacePequeno } from './estilos';

const TransporteCliente = ({
  presupuesto, estadoReglas, accionEnCurso = null, avisoTransporte = null, abierto = false, onAbrir,
  onAnadirTransporte, onTransporteManual, onTransporteAutomatico, onMadridCentral, onCobrarTransporte,
}) => {
  const [importeLibre, setImporteLibre] = useState('');
  const t = estadoReglas?.transporte || null;
  const frase = textoTransporteCliente(t);
  const cobrar = t ? !!(t.cobrar ?? t.linea) : false;
  const pista = pistaMadridCentral(presupuesto?.direccion_exacta || presupuesto?.lugar_evento);
  const ocupado = !!accionEnCurso;
  // Fila rápida: con transporte cobrado y línea en el presupuesto se edita el
  // importe; si no, se ofrece cobrarlo directamente (20/30/40 €, libre o zona).
  const conLinea = !!(cobrar && t?.linea);
  const importesRapidos = t?.importes_rapidos || [20, 30, 40];

  let textoZona = null;
  if (t && !t.disponible) textoZona = t.motivo;
  else if (t && t.zona_gratuita) textoZona = `${t.municipio || 'Zona'}: zona gratuita${t.confianza === 'baja' ? ' (revisa la dirección)' : ''}.`;
  else if (t) textoZona = `${t.municipio || 'Municipio sin detectar'}: zona ${t.zona} (${eurosCorto(t.importe)}).`;

  // Madrid Central: detección automática por coordenadas (backend). Dentro, la
  // línea se añade sola; se puede quitar (y volver a añadir si se quitó).
  const lineaMc = t?.linea_madrid_central || null;
  const dentroMc = t?.madrid_central_dentro;
  let textoMc = 'No entra en Madrid Central.';
  let botonMc = null; // { accion, texto }
  if (dentroMc === true && lineaMc) {
    textoMc = t.madrid_central_auto
      ? `Suplemento Madrid Central ${eurosCorto(lineaMc.total)} añadido automáticamente.`
      : `Dentro de Madrid Central: suplemento de ${eurosCorto(lineaMc.total)} añadido.`;
    botonMc = { accion: 'desactivar', texto: 'Quitar' };
  } else if (dentroMc === true) {
    textoMc = t.madrid_central_quitado_manual
      ? 'Dentro de Madrid Central sin suplemento (quitado a mano).'
      : 'Dentro de Madrid Central sin suplemento.';
    botonMc = { accion: 'activar', texto: 'Añadir' };
  } else if (dentroMc === false) {
    textoMc = lineaMc ? 'Fuera de Madrid Central, con el suplemento puesto a mano.' : 'No entra en Madrid Central.';
  } else if (t?.madrid_central) textoMc = 'Entra en Madrid Central.';
  else if (pista === 'puede') textoMc = 'Por el código postal puede estar en Madrid Central: revísalo.';

  const aplicarLibre = () => {
    const v = parseFloat(importeLibre);
    if (!(v > 0)) return;
    onTransporteManual(v);
    setImporteLibre('');
  };

  return (
    <div className="flex flex-col gap-3" data-testid="l2-transporte">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#16221D]" data-testid="l2-transporte-frase">{frase.texto}</p>
          {frase.detalle && <p className="text-xs text-[#5B6B64]" data-testid="l2-transporte-detalle">{frase.detalle}</p>}
          {onTransporteManual && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-[#5B6B64]" data-testid="l2-transporte-rapido">
              <span>{conLinea ? 'Transporte:' : 'Cobrar transporte:'}</span>
              {importesRapidos.map(imp => (
                <button key={imp} type="button" onClick={() => onTransporteManual(imp)} disabled={ocupado || !t}
                  className={`${botonSecundario} ${conLinea && Number(t?.importe_manual) === Number(imp) ? 'border-duetto-primary bg-[#E3F3EC]' : ''}`}
                  data-testid={`l2-transporte-rapido-${imp}`}>
                  {eurosCorto(imp)}
                </button>
              ))}
              <span className="inline-flex items-center gap-1.5">
                <input type="number" min="0" step="0.5" value={importeLibre}
                  onChange={(e) => setImporteLibre(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aplicarLibre(); } }}
                  placeholder="Otro" disabled={ocupado || !t}
                  className="w-20 px-2 py-1.5 min-h-[44px] md:min-h-0 text-base md:text-sm text-right border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary tabular-nums"
                  aria-label="Otro importe de transporte en euros" data-testid="l2-transporte-rapido-libre" />
                <button type="button" onClick={aplicarLibre} disabled={ocupado || !t || !(parseFloat(importeLibre) > 0)}
                  className={botonSecundario} data-testid="l2-transporte-rapido-aplicar">
                  Aplicar
                </button>
              </span>
              {conLinea && onCobrarTransporte && (
                <button type="button" onClick={() => onCobrarTransporte(false)} disabled={ocupado || !t}
                  className={enlacePequeno} data-testid="l2-transporte-rapido-quitar">
                  Quitar
                </button>
              )}
            </div>
          )}
          <p className="text-xs text-[#5B6B64]" data-testid="l2-transporte-mc">
            {textoMc}
            {botonMc && onMadridCentral && (
              <button type="button" onClick={() => onMadridCentral(botonMc.accion)} disabled={ocupado}
                className={`ml-2 ${enlacePequeno}`} data-testid={`l2-transporte-mc-${botonMc.accion === 'activar' ? 'anadir' : 'quitar'}`}>
                {botonMc.texto}
              </button>
            )}
          </p>
        </div>
        {onAbrir && (
          <button type="button" onClick={() => onAbrir(!abierto)} className={enlacePequeno} data-testid="l2-transporte-cambiar">
            {abierto ? 'Cerrar' : 'Cambiar'}
          </button>
        )}
      </div>

      {abierto && (
        <div className="flex flex-col gap-3 rounded-lg border border-[#E1E6E3] px-3.5 py-3" data-testid="l2-transporte-editor">
          {/* Cobrar sí o no */}
          {onCobrarTransporte && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-semibold text-[#16221D]" id="l2-transporte-cobrar">¿Se cobra transporte al cliente?</span>
              <div role="radiogroup" aria-labelledby="l2-transporte-cobrar" className="inline-flex rounded-lg border border-[#CBD3CF] overflow-hidden">
                {[{ valor: false, texto: 'No' }, { valor: true, texto: 'Sí' }].map(({ valor, texto }) => {
                  const activo = cobrar === valor;
                  return (
                    <button key={texto} type="button" role="radio" aria-checked={activo}
                      onClick={() => { if (!activo) onCobrarTransporte(valor); }}
                      disabled={ocupado || !t}
                      className={`h-11 md:h-[34px] px-4 text-sm md:text-[13px] font-bold disabled:opacity-50 disabled:cursor-not-allowed ${activo ? 'bg-duetto-primary text-white' : 'bg-white text-[#16221D] hover:bg-gray-50'}`}
                      data-testid={`l2-transporte-cobrar-${valor ? 'si' : 'no'}`}>
                      {texto}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {cobrar && (
            <>
              {/* Precio de la zona */}
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <span className="text-[#33443C]" data-testid="l2-transporte-zona">{textoZona || 'Calculando la zona...'}</span>
                {t?.importe_manual ? (
                  onTransporteAutomatico && (
                    <button type="button" onClick={onTransporteAutomatico} disabled={ocupado} className={enlacePequeno} data-testid="l2-transporte-usar-zona">
                      Usar el precio de la zona
                    </button>
                  )
                ) : t?.linea ? (
                  <span className="flex items-center gap-3">
                    <span className="text-xs text-[#0F5E43]">Ya está en el presupuesto</span>
                    <button type="button" onClick={onAnadirTransporte} disabled={ocupado} className={enlacePequeno} data-testid="l2-transporte-recalcular">
                      {accionEnCurso === 'transporte' ? 'Calculando...' : 'Recalcular por zona'}
                    </button>
                  </span>
                ) : (
                  <button type="button" onClick={onAnadirTransporte} disabled={ocupado} className={botonPrincipal} data-testid="l2-transporte-anadir">
                    {accionEnCurso === 'transporte' ? 'Calculando...' : 'Añadir transporte'}
                  </button>
                )}
              </div>

              {/* Otro importe */}
              {onTransporteManual && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold text-[#16221D]">Otro importe</span>
                  {(t?.importes_rapidos || [20, 30, 40]).map(imp => (
                    <button key={imp} type="button" onClick={() => onTransporteManual(imp)} disabled={ocupado}
                      className={`${botonSecundario} ${Number(t?.importe_manual) === Number(imp) ? 'border-duetto-primary bg-[#E3F3EC]' : ''}`}
                      data-testid={`l2-transporte-manual-${imp}`}>
                      {eurosCorto(imp)}
                    </button>
                  ))}
                  <span className="inline-flex items-center gap-1.5">
                    <input type="number" min="0" step="0.5" value={importeLibre}
                      onChange={(e) => setImporteLibre(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aplicarLibre(); } }}
                      placeholder="Otro"
                      className="w-20 px-2 py-1.5 min-h-[44px] md:min-h-0 text-base md:text-sm text-right border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary tabular-nums"
                      aria-label="Importe del transporte en euros" data-testid="l2-transporte-libre" />
                    <button type="button" onClick={aplicarLibre} disabled={ocupado || !(parseFloat(importeLibre) > 0)}
                      className={botonSecundario} data-testid="l2-transporte-aplicar">
                      Aplicar
                    </button>
                  </span>
                  {t?.importe_manual ? <span className="text-xs text-[#5B6B64]">Importe puesto a mano.</span> : null}
                </div>
              )}
            </>
          )}

          {/* Madrid Central */}
          {onMadridCentral && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <label className="inline-flex items-center gap-2 font-semibold text-[#16221D] cursor-pointer">
                <input type="checkbox" checked={!!lineaMc}
                  onChange={(e) => onMadridCentral(e.target.checked ? 'activar' : 'desactivar')}
                  disabled={ocupado || !t} className="h-4 w-4 accent-duetto-primary" data-testid="l2-transporte-mc-casilla" />
                Suplemento Madrid Central (+{eurosCorto(t?.suplemento_madrid_central ?? 30)})
                {t?.madrid_central_auto && <span className="text-xs font-normal text-[#5B6B64]">automático</span>}
              </label>
              {t?.linea_madrid_central && (
                <button type="button" onClick={() => onMadridCentral(t.linea_madrid_central.es_cortesia ? 'quitar_regalo' : 'regalar')}
                  disabled={ocupado} className={enlacePequeno} data-testid="l2-transporte-mc-regalar">
                  {t.linea_madrid_central.es_cortesia ? 'Volver a cobrarlo' : 'Regalar'}
                </button>
              )}
            </div>
          )}

          {t?.multievento && (
            <p className="text-xs text-[#5B6B64]" data-testid="l2-transporte-multievento">Multievento: la línea de transporte es una para todo el evento y la zona sale de la dirección principal. La ruta, la zona y Madrid Central de cada jornada se ven eligiendo la jornada arriba; el suplemento de Madrid Central lleva una unidad por jornada dentro de la zona.</p>
          )}
          {onAbrir && (
            <button type="button" onClick={() => onAbrir(false)} className={`self-start ${enlace}`}>Listo</button>
          )}
        </div>
      )}

      {avisoTransporte && (
        <p className={`text-xs ${avisoTransporte.tipo === 'error' ? 'text-[#8A1C12]' : 'text-[#0F5E43]'}`} data-testid="l2-transporte-mensaje">
          {avisoTransporte.texto}
        </p>
      )}
    </div>
  );
};

export default TransporteCliente;
